import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Plus, RotateCcw, Sparkles, User, FileSpreadsheet, Copy, Pencil, ArrowDownUp, Loader2 } from 'lucide-react';
import { useOutletContext, useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

import QuotationTable from './QuotationTable';
import ProductSearchModal from './ProductSearchModal';
import CopyToSelectionModal from './CopyToSelectionModal';
import ClientSelectModal from './ClientSelectModal';
import ExcelImportModal from './ExcelImportModal';
import DefaultsPopover from './DefaultsPopover';
import DraftDialog from './DraftDialog';
import SaveProgressModal from './SaveProgressModal';
import StockConfirmationModal from './StockConfirmationModal';
import UserTopbar from '../UserTopbar';
import Apiurl from '../../Helpers/apiUrl';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMasterProduct } from '../../redux/slices/productSlice';
import {
  setUnsavedQuoteState,
  setAiChatHistory,
  clearAiChatHistory,
  setIsAiOpen,
  resetAiState,
} from '../../redux/slices/aiSlice';
import StockLable from '../utils/StockLable';
import NotificationDrawer from '../NotificationDrawer';
import { ToastContainer, toast } from 'react-toastify';
import SmartSearchButton from '../utils/SmartSearchButton';
import { AutoAwesome, Save } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { Tooltip } from '@mui/material';
import AiPromptModal from './AiPromptModal';
import AiPreviewModal from './AiPreviewModal';
import { getSelectedCurrency,CURRENCY_SYMBOLS, convertAmount, formatCurrency } from '../../Helpers/currencyHelper';

const R2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

let _idCounter = 0;
const genId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  _idCounter = (_idCounter + 1) % 1_000_000;
  return `${Date.now()}-${_idCounter}-${Math.random().toString(36).slice(2, 9)}`;
};

const makeBlankRow = () => ({
  id: genId(),
  apiId: null,
  productCode: '', area: '', brand: '', description: '', collection: '', preferences:'', pruning_date: '', status: '',
  productType: '', category: '', priceSegment: '', finish: '', image: null, linediagram_image: null,
  quantity: 0, mrp: 0, basePrice: 0,
  discountPercent: 0, discountPrice: 0,
  gstRate: 18, totalDiscount: 0, total: 0,
  stock: null, stockIndicator: '',
  copyId: null,
});

const calculateRow = (row, field, value, gstType, discType) => {
  const r = { ...row, [field]: value };
  const qty = Number(r.quantity) || 0;
  const mrp = Number(r.mrp) || 0;
  const base = Number(r.basePrice) || 0;

  const priceBase = gstType === 'INC' ? mrp : base;

  if (discType === 'percent') {
    r.discountPrice = R2(priceBase * (Number(r.discountPercent) || 0) / 100);
  } else {
    r.discountPercent = R2(priceBase > 0 ? ((Number(r.discountPrice) || 0) / priceBase) * 100 : 0);
  }

  r.discountPercent = R2(r.discountPercent);
  r.discountPrice = R2(r.discountPrice);
  r.totalDiscount = R2(r.discountPrice * qty);
  r.total = R2((priceBase - r.discountPrice) * qty);

  return r;
};

const makeFreshSelections = () => Array.from({ length: 15 }, (_, i) => ({
  id: i + 1,
  name: `Selection ${i + 1}`,
  rows: i === 0 ? [makeBlankRow()] : [],
  discountType: 'percent',
  sorting: [],
}));

const DEFAULT_GST_TYPE = 'INC';
const MAX_SKUS_PER_SELECTION = 300;
const MAX_SELECTION_NAME_LENGTH = 31;

const getToken = () => localStorage.getItem('u_token') || '';

const AUTOSAVE_IDLE_MS = 60_000;

const mapApiRowToInternal = (apiRow, gstType, discType, skuMap) => {
  const code = (apiRow.sku_code || apiRow.productCode || '').trim();
  const productDetails = skuMap && code
    ? skuMap.get(code.toLowerCase())
    : null;

  const mrp = Number(apiRow.mrp) || Number(productDetails?.mrp) || 0;
  const gstRate = Number(productDetails?.gst) || Number(apiRow.gst) || 18;
  const basePrice = Number(productDetails?.basePrice) || (mrp > 0 && gstRate ? R2((mrp * 100) / (100 + gstRate)) : 0);
  const qty = Number(apiRow.qty) || Number(apiRow.quantity) || 1;
  const total = Number(apiRow.total) || 0;

  const priceBase = gstType === 'INC' ? mrp : basePrice;
  const discountPrice = qty > 0 ? R2(priceBase - (total / qty)) : 0;
  const discountPercent = priceBase > 0 ? R2((discountPrice / priceBase) * 100) : 0;
  const totalDiscount = R2(discountPrice * qty);

  return {
    ...makeBlankRow(),
    id: genId(),
    apiId: apiRow.id ?? null,
    productCode: code,
    area: apiRow.area || '',
    quantity: qty,
    total,
    brand: productDetails?.brand || apiRow.brand || '',
    description: productDetails?.product_desc || apiRow.product_desc || apiRow.description || '',
    collection: productDetails?.collection || apiRow.collection || '',
    preferences: productDetails?.preferences || apiRow.preferences || '',
    pruning_date: productDetails?.pruning_date || apiRow.pruning_date || '',
    status: productDetails?.status || apiRow.status || '',
    finish: productDetails?.finish || apiRow.finish || '',
    productType: productDetails?.product_typ_group || apiRow.product_typ_group || apiRow.productType || '',
    category: productDetails?.category || apiRow.category || '',
    priceSegment: productDetails?.priceSegment || apiRow.priceSegment || '',
    stock: productDetails?.stockAvailable || apiRow.stockAvailable || apiRow.stock || '',
    stockIndicator: productDetails?.stockIndicator || apiRow.stockIndicator || '',
    image: productDetails?.image || apiRow.image || (code ? `/img/${code}.jpg` : null),
    linediagram_image: productDetails?.linediagram_image || apiRow.linediagram_image || null,
    mrp,
    basePrice,
    gstRate,
    discountPercent,
    discountPrice,
    totalDiscount,
  };
};

const smartSortRows = (rows) => {
  const hasArea = rows.some(r => (r.area || '').trim() !== '');

  return [...rows].sort((a, b) => {
    if (hasArea) {
      const brandCmp = (a.brand || '').toLowerCase().localeCompare((b.brand || '').toLowerCase());
      if (brandCmp !== 0) return brandCmp;
      const aArea = (a.area || '').trim();
      const bArea = (b.area || '').trim();
      if (!aArea && !bArea) return 0;
      if (!aArea) return 1;
      if (!bArea) return -1;
      return aArea.toLowerCase().localeCompare(bArea.toLowerCase());
    } else {
      const aKey = `${a.brand || ''} ${a.priceSegment || ''}`.toLowerCase().trim();
      const bKey = `${b.brand || ''} ${b.priceSegment || ''}`.toLowerCase().trim();
      return aKey.localeCompare(bKey);
    }
  });
};

const getZeroStockSkus = (masterProduct, skuCodes) => {
  const codes = Array.isArray(skuCodes) ? skuCodes : [skuCodes];
  return codes.filter(sku => {
    if (!sku) return false;
    const product = masterProduct.find(p => p.sku_code === sku);
    if (!product) return false;
    const stock = product.stockAvailable;
    if (stock === null || stock === undefined || stock === '') return false;
    return Number(stock) === 0;
  });
};

const pickRowsForCopy = (selectionsList, srcId, selectedProducts) => {
  const srcRows = (selectionsList.find(s => s.id === srcId)?.rows || []).filter(r => r.productCode);
  const allowed = selectedProducts ? new Set(selectedProducts[srcId] || []) : null;

  const seen = new Set();
  return srcRows.filter(r => {
    if (allowed && !allowed.has(r.productCode)) return false;
    if (seen.has(r.productCode)) return false;
    seen.add(r.productCode);
    return true;
  });
};

export default function CreateQuote() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isSidebarOpen } = useOutletContext();
  const { masterProduct = [], getStatus } = useSelector((state) => state.product);
  const isCatalogReady = masterProduct && masterProduct.length > 0;
  const isProductLoading = getStatus === 'pending' || getStatus === 'idle';

  useEffect(() => {
    if (!masterProduct || masterProduct.length === 0) {
      dispatch(fetchMasterProduct());
    }
  }, [dispatch, masterProduct]);

  const skuMap = useMemo(() => {
    const map = new Map();
    masterProduct.forEach(p => {
      const key = (p.sku_code || '').trim().toLowerCase();
      if (key) map.set(key, p);
    });
    return map;
  }, [masterProduct]);
  const skuMapRef = useRef(skuMap);
  skuMapRef.current = skuMap;
  const { clientOpt = [] } = useSelector(state => state.options);
  const { showStockWarning = true } = useSelector(state => state.pdfImages || {});

  const { cid, clientName, projectId } = useParams();

  const location = useLocation();
  const uid = location.state?.uid;
  const [activeSkuForSuggestion, setActiveSkuForSuggestion] = useState(null);

  const isDraftParam = projectId?.startsWith('draft-');
  const paramDraftId = isDraftParam ? projectId.replace('draft-', '') : null;
  const paramProjectId = (!isDraftParam && projectId && projectId !== 'new') ? projectId : null;

  const isExplicitEditParam = Boolean(paramProjectId && location.state?.selectionDetails?.length);
  const isExplicitTemplateParam = Boolean(location.state?.isTemplate);

  const {
    unsavedQuoteState = null,
    aiChatHistory = [],
    isAiOpen = false,
    isAiEnabled = false,
  } = useSelector((state) => state.ai || {});

  // Active currency state loaded from localStorage
  const [currentCurrency, setCurrentCurrency] = useState(() => getSelectedCurrency());

  // Listen for currency changes made in Settings or across tabs
  useEffect(() => {
    const handleStorageChange = () => {
      setCurrentCurrency(getSelectedCurrency());
    };
    window.addEventListener('storage', handleStorageChange);
    // Custom event listener for same-tab changes if dispatched
    window.addEventListener('quotation_currency_changed', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('quotation_currency_changed', handleStorageChange);
    };
  }, []);

  const getSavedState = () => {
    if (unsavedQuoteState) return unsavedQuoteState;
    try {
      const raw = sessionStorage.getItem('unsaved_quote_state');
      if (raw) return JSON.parse(raw);
    } catch (e) { }
    return null;
  };

  const initialSavedState = getSavedState();
  const shouldRestore = !paramDraftId && !isExplicitEditParam && !isExplicitTemplateParam && Boolean(initialSavedState);

  const [editProjectId, setEditProjectId] = useState(() => {
    if (isExplicitEditParam) return paramProjectId;
    if (shouldRestore) return initialSavedState?.editProjectId || initialSavedState?.paramProjectId || null;
    return null;
  });

  const [selectionDetails, setSelectionDetails] = useState(() => {
    if (isExplicitEditParam) return location.state.selectionDetails;
    if (shouldRestore) return initialSavedState?.selectionDetails || null;
    return null;
  });

  const [quoteDetails, setQuoteDetails] = useState(() => {
    if (isExplicitEditParam || isExplicitTemplateParam) return location.state.quoteDetails || null;
    if (shouldRestore) return initialSavedState?.quoteDetails || null;
    return null;
  });

  const [isTemplate, setIsTemplate] = useState(() => {
    if (isExplicitTemplateParam) return true;
    if (shouldRestore) return Boolean(initialSavedState?.isTemplate);
    return false;
  });

  const isEditMode = Boolean(editProjectId && selectionDetails?.length);

  const [draftUid, setDraftUid] = useState(() => {
    if (uid) return uid;
    if (shouldRestore) return initialSavedState?.draftUid || null;
    return null;
  });

  const [draftId, setDraftId] = useState(() => {
    if (paramDraftId) return paramDraftId;
    if (shouldRestore) return initialSavedState?.draftId || null;
    return null;
  });

  const [gstType, setGstType] = useState(() => {
    if (shouldRestore && initialSavedState?.gstType) return initialSavedState.gstType;
    return DEFAULT_GST_TYPE;
  });

  const [selections, setSelections] = useState(() => {
    if (shouldRestore && initialSavedState?.selections?.length) return initialSavedState.selections;
    return makeFreshSelections();
  });

  const [currentSelId, setCurrentSelId] = useState(() => {
    if (shouldRestore && initialSavedState?.currentSelId) return initialSavedState.currentSelId;
    return 1;
  });

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCopyOpen, setIsCopyOpen] = useState(false);
  const [isClientOpen, setIsClientOpen] = useState(false);
  const [isExcelOpen, setIsExcelOpen] = useState(false);
  const [isDraftOpen, setIsDraftOpen] = useState(false);
  const [editingName, setEditingName] = useState(null);
  const [tempName, setTempName] = useState('');
  const [validUpto, setValidUpto] = useState(() => {
    if (shouldRestore && initialSavedState?.validUpto) return initialSavedState.validUpto;
    return '';
  });

  const [selectedClient, setSelectedClient] = useState(() => {
    if (shouldRestore && initialSavedState?.selectedClient) return initialSavedState.selectedClient;
    if (cid && clientName && cid !== 'blank') {
      return { id: cid, client_name: decodeURIComponent(clientName) };
    }
    return null;
  });

  const [defaults, setDefaults] = useState(() => {
    if (shouldRestore && initialSavedState?.defaults) return initialSavedState.defaults;
    return { qty: 1, discountPercent: 0 };
  });

  const [autoSaveStatus, setAutoSaveStatus] = useState(null);
  const [isSaveProgressOpen, setIsSaveProgressOpen] = useState(false);
  const [saveProgressItems, setSaveProgressItems] = useState([]);
  const [loadingSelIds, setLoadingSelIds] = useState(new Set());
  const [removedApiIds, setRemovedApiIds] = useState(() => {
    if (shouldRestore && initialSavedState?.removedApiIds) {
      return Object.fromEntries(
        Object.entries(initialSavedState.removedApiIds).map(([k, v]) => [k, new Set(v)])
      );
    }
    return {};
  });

  const [savedProjectId, setSavedProjectId] = useState(null);
  const [visitedSelIds, setVisitedSelIds] = useState(() => {
    if (shouldRestore && initialSavedState?.visitedSelIds) return initialSavedState.visitedSelIds;
    return [1];
  });



  const [errorMessages, setErrorMessages] = useState([]);
  const [isDraftLoading, setIsDraftLoading] = useState(Boolean(paramDraftId));
  const [stockConfirm, setStockConfirm] = useState(null);

  const handleStockConfirm = useCallback((config) => {
    if (config === null) {
      setStockConfirm(null);
      return;
    }
    const mode = config.mode || "out_of_stock";
    if (!showStockWarning && mode === "out_of_stock") {
      config.onConfirm?.();
      return;
    }
    setStockConfirm(config);
  }, [showStockWarning]);
  const [aiPreviewData, setAiPreviewData] = useState(null);

  const nameInputRef = useRef();
  const addSkuInputRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const draftIdRef = useRef(paramDraftId || null);
  const getPayloadRef = useRef(null);
  const isDirtyRef = useRef(false);

  const selectionsRef = useRef(selections);
  useEffect(() => { selectionsRef.current = selections; }, [selections]);
  const gstTypeRef = useRef(gstType);
  useEffect(() => { gstTypeRef.current = gstType; }, [gstType]);
  const removedApiIdsRef = useRef(removedApiIds);
  useEffect(() => { removedApiIdsRef.current = removedApiIds; }, [removedApiIds]);
  useEffect(() => { draftIdRef.current = draftId; }, [draftId]);

  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success("Back online! Your internet connection is restored.", { id: 'network-status', duration: 4000 });
      if (isDirtyRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = setTimeout(() => {
          if (getPayloadRef.current) getPayloadRef.current().doAutoSave?.();
        }, 1000);
      }
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.error("You are offline. Auto-save is paused.", { id: 'network-status', duration: 8000 });
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (paramProjectId && location.state?.selectionDetails) {
      setEditProjectId(paramProjectId);
      setSelectionDetails(location.state.selectionDetails);
      setQuoteDetails(location.state.quoteDetails || null);
      setIsTemplate(Boolean(location.state.isTemplate));
    }
  }, [paramProjectId, location.state]);

  useEffect(() => {
    const serializedRemovedApiIds = Object.fromEntries(
      Object.entries(removedApiIds).map(([k, v]) => [k, Array.from(v || [])])
    );

    const stateToSave = {
      isEditMode,
      editProjectId,
      paramProjectId: editProjectId,
      selectionDetails,
      quoteDetails,
      cid: cid || selectedClient?.id || null,
      clientName: clientName || selectedClient?.client_name || null,
      selectedClient,
      validUpto,
      gstType,
      selections,
      currentSelId,
      removedApiIds: serializedRemovedApiIds,
      visitedSelIds,
      draftId,
      draftUid,
      defaults,
      isTemplate,
    };

    dispatch(setUnsavedQuoteState(stateToSave));
    try {
      sessionStorage.setItem('unsaved_quote_state', JSON.stringify(stateToSave));
    } catch (e) { }
  }, [
    isEditMode,
    editProjectId,
    selectionDetails,
    quoteDetails,
    cid,
    clientName,
    selectedClient,
    validUpto,
    gstType,
    selections,
    currentSelId,
    removedApiIds,
    visitedSelIds,
    draftId,
    draftUid,
    defaults,
    isTemplate,
    dispatch
  ]);

  const removeErrorMessage = (skuCode) => {
    setErrorMessages((prev) => prev.filter((msg) => msg.sku_code !== skuCode));
  };

  const setSelectionsDirty = useCallback((fn) => {
    isDirtyRef.current = true;
    setSelections(fn);
  }, []);

  useEffect(() => {
    if (cid && clientName && cid !== 'blank') {
      const decoded = decodeURIComponent(clientName);
      setSelectedClient(prev => {
        if (prev?.id === cid && prev?.client_name === decoded) return prev;
        return { id: cid, client_name: decoded };
      });
    }
  }, [cid, clientName]);

  useEffect(() => {
    if (!paramDraftId) return;
    const load = async () => {
      setIsDraftLoading(true);
      try {
        const { data } = await axios.get(
          `${Apiurl}project/draft/edit/${paramDraftId}/1`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        const draft = data?.data || {};
        if (!draft || !draft.section) return;

        const firstSection = Object.values(draft.section)[0];
        const sampleProduct = firstSection?.[0];
        const derivedGstType = Number(sampleProduct?.gst) != 0 ? 'INC' : 'EXC';
        setGstType(derivedGstType);

        const mappedSelections = Object.keys(draft.section).map((key) => {
          const rows = draft.section[key];
          return {
            id: Number(key),
            name: rows[0]?.section_name || `Selection ${key}`,
            discountType: 'percent',
            sorting: [],
            rows: rows.map(r => {
              const attr = r.product_attr || r.productAttr || {};
              return mapApiRowToInternal(
                {
                  id: r.id,
                  sku_code: r.sku_code,
                  qty: Number(r.qty) || 1,
                  mrp: Number(attr.mrp) || Number(r.mrp) || 0,
                  area: r.area || '',
                  total: Number(r.total) || 0,
                  gst: Number(r.gst) || Number(attr.gst) || 0,
                  brand: attr.brand || r.brand || '',
                  product_desc: attr.product_desc || r.product_desc || '',
                  description: attr.product_desc || r.description || '',
                  collection: attr.collection || r.collection || '',
                  preferences: attr.preferences || r.preferences || '', 
                  category: attr.category || r.category || '',
                  finish: attr.finish || r.finish || '',
                  product_typ_group: attr.product_typ_group || r.product_typ_group || '',
                  productType: attr.product_typ_group || r.productType || '',
                  priceSegment: attr.priceSegment || r.priceSegment || '',
                  stockAvailable: attr.stockAvailable || r.stockAvailable || '',
                  stockIndicator: attr.stockIndicator || r.stockIndicator || '',
                  image: attr.image || r.image || null,
                  linediagram_image: attr.linediagram_image || r.linediagram_image || null,
                },
                derivedGstType,
                'percent',
                skuMapRef.current || skuMap
              );
            })
          };
        });

        setSelections(prev => {
          const updated = prev.map(s => {
            const match = mappedSelections.find(m => m.id === s.id);
            return match ? match : s;
          });
          mappedSelections.forEach(ms => {
            if (!updated.some(s => s.id === ms.id)) {
              updated.push(ms);
            }
          });
          return updated;
        });

        if (mappedSelections.length > 0 && mappedSelections[0]?.id) {
          setCurrentSelId(mappedSelections[0].id);
        }

        setDraftId(paramDraftId);
        draftIdRef.current = paramDraftId;
        if (draft.uid) {
          setDraftUid(draft.uid);
        }

        if (draft.client_id) {
          const clientName = draft.client?.client_name || clientOpt?.find((c) => c.id == draft.client_id)?.client_name || '';
          setSelectedClient({ id: draft.client_id, client_name: clientName });
        }
        if (draft.valid_date) {
          setValidUpto(draft.valid_date);
        }
      } catch (err) {
        console.error('Failed to load draft:', err);
      } finally {
        setIsDraftLoading(false);
      }

    };
    load();
  }, [paramDraftId]);

  useEffect(() => {
    const isTempl = location.state?.isTemplate;
    if ((!isEditMode && !isTempl) || !location.state?.selectionDetails) return;

    const fetchSequentially = async () => {
      if (quoteDetails && !isTempl) {
        setTimeout(() => {
          if (typeof quoteDetails.ai_chat_history === 'string' && quoteDetails.ai_chat_history.length > 2) {
            try {
              const parsedHistory = JSON.parse(quoteDetails.ai_chat_history);
              const lightweightHistory = parsedHistory.map(chat => {
                if (chat.payload) {
                  return {
                    ...chat,
                    payload: chat.payload.map(sel => ({
                      ...sel,
                      products: []
                    }))
                  };
                }
                return chat;
              });
              setAiChatHistory(lightweightHistory);
            } catch (e) {
              console.error("Failed to parse ai_chat_history", e);
              setAiChatHistory([]);
            }
          } else {
            setAiChatHistory([]);
          }
        }, 1500);
      }

      const activeSelDetails = location.state?.selectionDetails || selectionDetails || [];
      const loadingIds = new Set(activeSelDetails.map(s => s.selectionId));
      setLoadingSelIds(loadingIds);

      setSelections(makeFreshSelections().map(sel => {
        const match = activeSelDetails.find(s => s.selectionId === sel.id);
        return match ? { ...sel, name: match.selectionName } : sel;
      }));

      if (activeSelDetails.length > 0) {
        setCurrentSelId(activeSelDetails[0].selectionId);
      }

      const resolvedGstType = quoteDetails?.gstType || 'INC';
      setGstType(resolvedGstType);

      if (isTempl) {
        setValidUpto('');
        if (!cid || cid === 'blank') {
          setSelectedClient(null);
        }
      } else if (quoteDetails?.valid_date) {
        setValidUpto(quoteDetails.valid_date);
      }

      const fetchProjectId = isTempl && quoteDetails?.id ? quoteDetails.id : (editProjectId || paramProjectId);
      const discType = 'percent';

      // Load sequentially to unblock UI instantly after first selection!
      for (let i = 0; i < activeSelDetails.length; i++) {
        const sel = activeSelDetails[i];
        try {
          const { data } = await axios.get(
            `${Apiurl}project/summary-report/edit/${fetchProjectId}/${sel.selectionId}`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );

          const apiRows = data?.data || [];

          if (!isTempl && i === 0 && apiRows.length > 0 && apiRows[0].valid_upto) {
            setValidUpto(apiRows[0].valid_upto);
          }

          const mappedRows = Array.isArray(apiRows) && apiRows.length > 0
            ? apiRows.map(r => mapApiRowToInternal(r, resolvedGstType, discType, skuMapRef.current || skuMap))
            : [makeBlankRow()];

          setSelections(prev => prev.map(s => {
            if (s.id === sel.selectionId) {
              return { ...s, name: sel.selectionName, rows: mappedRows, discountType: discType, sorting: [] };
            }
            return s;
          }));

        } catch (err) {
          console.error(`Failed to load selection ${sel.selectionId}:`, err);
        } finally {
          setLoadingSelIds(prev => {
            const next = new Set(prev);
            next.delete(sel.selectionId);
            return next;
          });
        }
      }
    };

    fetchSequentially();
  }, [isEditMode, location.key]);

  useEffect(() => {
    if (skuMap && skuMap.size > 0 && selections.length > 0) {
      let needsHydration = false;
      const hydratedSelections = selections.map(sel => {
        let hydratedRows = false;
        const newRows = sel.rows.map(row => {
          const code = (row.productCode || '').trim();
          if (code) {
            const productDetails = skuMap.get(code.toLowerCase());
            if (productDetails) {
              const needsDesc = productDetails.product_desc && !row.description;
              const needsImage = productDetails.image && !row.image;
              const needsLineImage = productDetails.linediagram_image && !row.linediagram_image;
              const needsCollection = productDetails.collection && !row.collection;
              const needsPreferences = productDetails.preferences && !row.preferences;
              const needsBrand = productDetails.brand && !row.brand;
              const needsType = productDetails.product_typ_group && !row.productType;
              const needsCat = productDetails.category && !row.category;
              const needsFinish = productDetails.finish && !row.finish;
              const needsSegment = productDetails.priceSegment && !row.priceSegment;

              if (needsDesc || needsImage || needsLineImage || needsCollection || needsBrand || needsType || needsCat || needsFinish || needsSegment) {
                hydratedRows = true;
                needsHydration = true;
                return {
                  ...row,
                  brand: row.brand || productDetails.brand || '',
                  description: row.description || productDetails.product_desc || '',
                  collection: row.collection || productDetails.collection || '',
                  preferences: row.preferences || productDetails.preferences || '',
                  pruning_date: row.pruning_date || productDetails.pruning_date || '',
                  status: row.status || productDetails.status || '',
                  finish: row.finish || productDetails.finish || '',
                  productType: row.productType || productDetails.product_typ_group || '',
                  category: row.category || productDetails.category || '',
                  priceSegment: row.priceSegment || productDetails.priceSegment || '',
                  stock: row.stock || productDetails.stockAvailable || '',
                  stockIndicator: row.stockIndicator || productDetails.stockIndicator || '',
                  image: row.image || productDetails.image || (code ? `/img/${code}.jpg` : null),
                  linediagram_image: row.linediagram_image || productDetails.linediagram_image || null,
                  gstRate: row.gstRate || Number(productDetails.gst) || 18,
                  basePrice: row.basePrice || Number(productDetails.basePrice) || (row.mrp && row.gstRate ? R2((row.mrp * 100) / (100 + Number(row.gstRate))) : 0),
                };
              }
            }
          }
          return row;
        });
        return hydratedRows ? { ...sel, rows: newRows } : sel;
      });
      if (needsHydration) {
        setSelections(hydratedSelections);
      }
    }
  }, [skuMap, selections]);

  useEffect(() => {
    const doAutoSave = async () => {

      if (!isDirtyRef.current) return;
      if (!navigator.onLine) {
        setAutoSaveStatus('error');
        return;
      }

      const payload = getPayloadRef.current?.();
      if (!payload || !payload.tableData?.length) return;

      const headers = { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' };
      setAutoSaveStatus('saving');
      try {
        const currentDraftId = draftIdRef.current;
        if (currentDraftId) {
          await axios.post(`${Apiurl}project/draft/update`, { ...payload, draftId: currentDraftId }, { headers });
        } else {
          const { data } = await axios.post(`${Apiurl}project/draft/add`, payload, { headers });
          const uid = data?.uid || data?.data?.uid || '';
          const id = data?.project_id || data?.data?.project_id;
          if (id) { draftIdRef.current = String(id); setDraftId(String(id)); setDraftUid(String(uid)) }
        }

        const resolvedDraftId = draftIdRef.current;
        if (!resolvedDraftId) throw new Error('No draft ID after save');

        await new Promise(res => setTimeout(res, 800));

        const { data: freshDraft } = await axios.get(
          `${Apiurl}project/draft/edit/${resolvedDraftId}/1`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );

        const section = freshDraft?.data?.section || {};
        const apiIdMap = {};
        Object.keys(section).forEach(selId => {
          apiIdMap[selId] = {};
          section[selId].forEach(r => {
            if (r.sku_code && r.id) apiIdMap[selId][r.sku_code] = r.id;
          });
        });

        setSelections(prev => prev.map(sel => {
          const map = apiIdMap[String(sel.id)];
          if (!map) return sel;
          return {
            ...sel,
            rows: sel.rows.map(r => ({
              ...r,
              apiId: r.productCode && map[r.productCode] ? map[r.productCode] : r.apiId,
            })),
          };
        }));

        isDirtyRef.current = false;
        setAutoSaveStatus('saved');
      } catch {
        setAutoSaveStatus('error');
      } finally {
        setTimeout(() => setAutoSaveStatus(null), 3000);
      }
    };

    const resetTimer = () => {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(doAutoSave, AUTOSAVE_IDLE_MS);
    };

    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keydown', resetTimer);
    return () => {
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keydown', resetTimer);
      clearTimeout(autoSaveTimerRef.current);
    };
  }, []);

  const currentSel = selections.find(s => s.id === currentSelId);

  const updateSel = (selId, patch) =>
    setSelectionsDirty(p => p.map(s => s.id === selId ? { ...s, ...patch } : s));

  const updateRows = (selId, fn) =>
    setSelectionsDirty(p => p.map(s => s.id === selId ? { ...s, rows: fn(s.rows) } : s));

  const switchSelection = (id) => {
    const numId = Number(id);
    setCurrentSelId(numId);
    setSelectionsDirty(p => p.map(s =>
      s.id === numId && s.rows.length === 0 ? { ...s, rows: [makeBlankRow()] } : s
    ));
    setVisitedSelIds(prev =>
      prev.includes(numId) ? prev : [...prev, numId]
    );
  };

  const startRename = (sel, e) => {
    e.stopPropagation();
    setEditingName(sel.id); setTempName(sel.name);
    setTimeout(() => nameInputRef.current?.focus(), 30);
  };

  const commitRename = () => {
    const invalidChars = /[*?:\\\/\[\]]/;

    if (invalidChars.test(tempName)) {
      toast.error('Selection name cannot contain * ? : \\ / [ ] characters');
      return;
    }

    if (tempName.trim().length > MAX_SELECTION_NAME_LENGTH) {
      toast.error(`Selection name cannot exceed ${MAX_SELECTION_NAME_LENGTH} characters.`);
      return;
    }

    if (tempName.trim()) updateSel(editingName, { name: tempName.trim() });
    setEditingName(null);
  };

  const toggleGstType = (type) => {
    setGstType(type);
    setSelectionsDirty(prev => prev.map(sel => ({
      ...sel,
      rows: sel.rows.map(r => calculateRow(r, 'gstType', type, type, sel.discountType)),
    })));
  };

  const recordRemovedApiId = useCallback((selId, apiId) => {
    if (!apiId) return;
    setRemovedApiIds(prev => {
      const existing = prev[selId] ? new Set(prev[selId]) : new Set();
      existing.add(apiId);
      return { ...prev, [selId]: existing };
    });
  }, []);

  const handleSelectionSortingChange = useCallback((newSorting) => {
    const selId = currentSelId;
    setSelectionsDirty(prev => prev.map(s =>
      s.id === selId ? { ...s, sorting: newSorting } : s
    ));
  }, [currentSelId]);

  const handleSmartSort = useCallback(() => {
    const selId = currentSelId;
    setSelectionsDirty(prev => prev.map(s => {
      if (s.id !== selId) return s;
      const sortedRows = smartSortRows(s.rows);
      return { ...s, rows: sortedRows, sorting: [] };
    }));
  }, [currentSelId]);



  const generateProjectId = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${year}${month}${day}${hours}${minutes}${seconds}`;
  };

  const resetAfterSave = useCallback(() => {
    setSelections(makeFreshSelections());
    setCurrentSelId(1);
    setSelectedClient(null);
    setValidUpto('');
    setDefaults({ qty: 1, discountPercent: 0 });
    setGstType(DEFAULT_GST_TYPE);
    setDraftId(null);
    setDraftUid(null);
    draftIdRef.current = null;
    setRemovedApiIds({});
    setVisitedSelIds([1]);
    setEditProjectId(null);
    setSelectionDetails(null);
    setQuoteDetails(null);
    setIsTemplate(false);
    dispatch(resetAiState());
    try {
      sessionStorage.removeItem('unsaved_quote_state');
    } catch (e) { }
  }, [dispatch]);

  useEffect(() => {
    const hasClientParam = cid && clientName && cid !== 'blank';
    const savedIsEdit = initialSavedState?.editProjectId || initialSavedState?.paramProjectId;
    if (!shouldRestore || !hasClientParam || !savedIsEdit) return;

    resetAfterSave();
    setSelectedClient({ id: cid, client_name: decodeURIComponent(clientName) });
  }, []);

  const validateQuote = useCallback((activeSelections, gstType) => {
    const errors = [];

    if (!selectedClient || selectedClient.client_name === "Select-Client") {
      toast.error('Please select a valid client.');
      return null;
    }

    if (!validUpto) {
      toast.error('Please set a valid upto date');
      return null;
    }

    activeSelections.forEach((sel) => {
      sel.rows.forEach((row) => {
        if (!row.productCode) return;

        const selLabel = String(sel.id);

        if (Number(row.quantity) < 1) {
          errors.push({
            message: `Quantity should not be zero for SKU ${row.productCode} in Selection: ${selLabel}`,
            sku_code: row.id,
            selection: selLabel,
          });
        }

        if (Number(row.mrp) === 0) {
          errors.push({
            message: `MRP cannot be zero! Please remove this SKU ${row.productCode} in Selection: ${selLabel}`,
            sku_code: row.id,
            selection: selLabel,
          });
        }

        const priceBase = gstType === 'INC' ? Number(row.mrp) : Number(row.basePrice);
        if (Number(row.discountPrice) > priceBase || Number(row.total) < 0) {
          errors.push({
            message: `Discount exceeds price, total cannot be negative for SKU ${row.productCode} in Selection: ${selLabel}`,
            sku_code: row.id,
            selection: selLabel,
          });
        }
      });
    });

    return errors;
  }, [selectedClient, validUpto]);

  const applyRowSorting = (rows, sorting) => {
    if (!sorting || sorting.length === 0) return rows;

    return [...rows].sort((a, b) => {
      for (const { id, desc } of sorting) {
        const aVal = a[id] ?? '';
        const bVal = b[id] ?? '';

        let cmp = 0;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          cmp = aVal - bVal;
        } else {
          cmp = String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase());
        }

        if (cmp !== 0) return desc ? -cmp : cmp;
      }
      return 0;
    });
  };

  const saveQuote = async (mode = 'new') => {
    const activeSelections = selections.filter(sel =>
      sel.rows.some(row => row.productCode.trim() !== '')
    );
    if (activeSelections.length === 0) {
      toast.warn('No products found in any selection to save.');
      return;
    }

    const defaultNamedSelections = activeSelections.filter(sel =>
      /^Selection\s+\d+$/i.test(sel.name.trim())
    );

    if (defaultNamedSelections.length > 0) {
      const nameList = defaultNamedSelections
        .map(s => `<span style="
      display:inline-block;
      background:#eff6ff;
      color:#1e40af;
      border:1.5px solid #bfdbfe;
      border-radius:6px;
      padding:2px 10px;
      font-size:12px;
      font-weight:600;
      margin:3px 4px;
    ">${s.name}</span>`)
        .join('');

      const result = await Swal.fire({
        title: 'Unnamed Selections',
        html: `
      <p style="color:#475569;font-size:13px;margin-bottom:12px;line-height:1.6">
        The following selection${defaultNamedSelections.length > 1 ? 's are' : ' is'} still using
        a default name. Consider renaming ${defaultNamedSelections.length > 1 ? 'them' : 'it'}
        for better clarity in the quotation.
      </p>
      <div style="margin:8px 0 4px">${nameList}</div>
    `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Save Anyway',
        cancelButtonText: 'Go Back & Rename',
        confirmButtonColor: '#1a56db',
        cancelButtonColor: '#64748b',
        reverseButtons: true,
        focusCancel: true,
        customClass: {
          popup: 'swal-quotation-popup',
        },
      });

      if (!result.isConfirmed) return;
    }

    const errors = validateQuote(activeSelections, gstType);
    if (errors === null) return;
    if (errors.length > 0) {
      setErrorMessages(errors);
      return;
    }

    setErrorMessages([]);

    let finalProjectId = '';
    let projUid = '';
    let postFix = '';
    if (mode === 'update' && editProjectId) {
      finalProjectId = editProjectId;
      projUid = quoteDetails?.project_id || editProjectId;
      postFix = 'project/summary/update';
    } else {
      postFix = 'project/store';
      const PID = generateProjectId();
      const clientNameClean = selectedClient.client_name?.replace(/[\/\\\.]/g, "")
        ?.replace(/\s+/g, "_")
        ?.toUpperCase();
      finalProjectId = `P_${clientNameClean}_${PID}`;
      projUid = `P_${clientNameClean}_${PID}`;
    }

    setSavedProjectId(projUid);

    if (mode === 'update') {
      const allRemovedIds = activeSelections.flatMap(sel =>
        Array.from(removedApiIds[sel.id] || new Set())
      );

      const tableData = activeSelections.flatMap(sel =>
        applyRowSorting(sel.rows, sel.sorting)
          .filter(row => row.productCode)
          .map(row => ({
            id: row.apiId ?? null,
            sku_code: row.productCode,
            quantity: Number(row.quantity) || 0,
            area: row.area || '',
            mrp: Number(row.mrp) || 0,
            discount_price: Number(row.discountPrice) || 0,
            discount_per: Number(row.discountPercent) || 0,
            gst: gstType === 'EXC' ? 0 : (Number(row.gstRate) || 0),
            total: String(row.total || 0),
            discount: Number(row.totalDiscount) || 0,
            selection: sel.id,
            selection_name: sel.name,
            unit: Number(row.basePrice) || 0,
            copy_id: 0,
            project_details_id: Number(finalProjectId),
          }))
      );

      const payload = {
        quatationId: finalProjectId,
        draftId: draftIdRef.current || null,
        projectId: finalProjectId,
        selectionClient: selectedClient.id,
        selectedDate: validUpto || null,
        removedIds: allRemovedIds,
        amountType: 'price',
        selectionHistoryForEdit: quoteDetails?.gstType !== gstType ? (selectionDetails || []).map(s => s.selectionId) : visitedSelIds,
        tableData,
        ai_chat_history: aiChatHistory,
      };

      setSaveProgressItems(activeSelections.map(sel => ({ id: sel.id, name: sel.name, status: 'saving', error: null })));
      setIsSaveProgressOpen(true);

      try {
        const { data: updateRes } = await axios.post(`${Apiurl}${postFix}`, payload, {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        });
        setSaveProgressItems(activeSelections.map(sel => ({ id: sel.id, name: sel.name, status: 'saved', error: null })));
        setRemovedApiIds({});
      } catch (err) {
        const msg = err?.response?.data?.message || 'Network error';
        setSaveProgressItems(activeSelections.map(sel => ({ id: sel.id, name: sel.name, status: 'error', error: msg })));
        return { success: false };
      }
      return { success: true, projectId: finalProjectId };
    }

    setSaveProgressItems(activeSelections.map(sel => ({ id: sel.id, name: sel.name, status: 'pending', error: null })));
    setIsSaveProgressOpen(true);

    let allSucceeded = true;
    let newDbId = null;

    for (const sel of activeSelections) {
      setSaveProgressItems(prev => prev.map(i => i.id === sel.id ? { ...i, status: 'saving' } : i));

      const removedSet = removedApiIds[sel.id] || new Set();
      const removedIdsArray = Array.from(removedSet);

      const payload = {
        quatationId: finalProjectId,
        draftId: draftIdRef.current || null,
        projectId: finalProjectId,
        selectionClient: selectedClient.id,
        selectedDate: validUpto || null,
        removedIds: removedIdsArray,
        amountType: 'price',
        selectionHistoryForEdit: visitedSelIds,
        tableData: applyRowSorting(sel.rows, sel.sorting)
          .filter(row => row.productCode)
          .map(row => ({
            id: row.apiId ?? null,
            sku_code: row.productCode,
            quantity: Number(row.quantity) || 0,
            area: row.area || '',
            mrp: Number(row.mrp) || 0,
            discount_price: Number(row.discountPrice) || 0,
            discount_per: Number(row.discountPercent) || 0,
            gst: gstType === 'EXC' ? 0 : (Number(row.gstRate) || 0),
            total: String(row.total || 0),
            discount: Number(row.totalDiscount) || 0,
            selection: String(sel.id),
            selection_name: sel.name,
            unit: Number(row.basePrice) || 0,
            copy_id: 0,
          })),
        ai_chat_history: aiChatHistory,
      };

      try {
        const { data: saveRes } = await axios.post(`${Apiurl}${postFix}`, payload, {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        });
        if (!newDbId && saveRes?.db_id) {
          newDbId = saveRes.db_id;
        }
        setSaveProgressItems(prev => prev.map(i => i.id === sel.id ? { ...i, status: 'saved' } : i));
        setRemovedApiIds(prev => { const n = { ...prev }; delete n[sel.id]; return n; });
      } catch (err) {
        allSucceeded = false;
        const msg = err?.response?.data?.message || 'Network error';
        setSaveProgressItems(prev => prev.map(i => i.id === sel.id ? { ...i, status: 'error', error: msg } : i));
      }
    }
    return { success: allSucceeded, projectId: finalProjectId };
  };

  const handleSaveModalClose = useCallback(() => {
    setIsSaveProgressOpen(false);
    resetAfterSave();
    navigate('/user/create-quote', { replace: true });
  }, [resetAfterSave, navigate]);

  const handleUpdateRow = useCallback((rowId, field, value) => {
    const selId = currentSelId;
    setSelectionsDirty(prev => prev.map(s => {
      if (s.id !== selId) return s;
      return {
        ...s,
        rows: s.rows.map(r => r.id === rowId
          ? calculateRow(r, field, value, gstTypeRef.current, s.discountType)
          : r
        )
      };
    }));
  }, [currentSelId]);

  const handleDeleteRow = useCallback((rowId) => {
    const selId = currentSelId;
    setSelectionsDirty(prev => prev.map(s => {
      if (s.id !== selId) return s;
      const target = s.rows.find(r => r.id === rowId);
      if (target?.apiId) recordRemovedApiId(selId, target.apiId);
      const rem = s.rows.filter(r => r.id !== rowId);
      return { ...s, rows: rem.length === 0 ? [makeBlankRow()] : rem };
    }));
  }, [currentSelId, recordRemovedApiId]);

  const handleInsertAfter = useCallback((rowId) => {
    const selId = currentSelId;
    setSelectionsDirty(prev => prev.map(s => {
      if (s.id !== selId) return s;
      const currentCount = s.rows.filter(r => r.productCode).length;
      if (currentCount >= MAX_SKUS_PER_SELECTION) {
        toast.warn(`Maximum ${MAX_SKUS_PER_SELECTION} products allowed per selection.`);
        return s;
      }
      const idx = s.rows.findIndex(r => r.id === rowId);
      const copy = [...s.rows];
      copy.splice(idx + 1, 0, makeBlankRow());
      return { ...s, rows: copy };
    }));
  }, [currentSelId]);

  const handleDeleteSelected = useCallback((ids) => {
    const selId = currentSelId;
    setSelectionsDirty(prev => prev.map(s => {
      if (s.id !== selId) return s;
      s.rows.forEach(r => { if (ids.includes(r.id) && r.apiId) recordRemovedApiId(selId, r.apiId); });
      const rem = s.rows.filter(r => !ids.includes(r.id));
      return { ...s, rows: rem.length === 0 ? [makeBlankRow()] : rem };
    }));
  }, [currentSelId, recordRemovedApiId]);


  const handleProductCodeChange = useCallback((rowId, oldApiId, oldCode, newCode) => {
    if (oldApiId && newCode && oldCode !== newCode) {
      recordRemovedApiId(currentSelId, oldApiId);
      setSelectionsDirty(prev => prev.map(s => {
        if (s.id !== currentSelId) return s;
        return { ...s, rows: s.rows.map(r => r.id === rowId ? { ...r, apiId: null } : r) };
      }));
    }

    if (!newCode) return;

    const zeroSkus = getZeroStockSkus(masterProduct, newCode);
    if (zeroSkus.length === 0) return;

    const masterEntry = masterProduct.find(p => p.sku_code === newCode);
    handleStockConfirm({
      products: [{
        productCode: newCode,
        brand: masterEntry?.brand || '',
        description: masterEntry?.product_desc || masterEntry?.description || '',
        mrp: masterEntry?.mrp || 0,
        image: masterEntry?.image || null,
      }],
      onConfirm: () => { },
      onCancel: () => {
        setSelectionsDirty(prev => prev.map(s => {
          if (s.id !== currentSelId) return s;
          return { ...s, rows: s.rows.map(r => r.id === rowId ? { ...makeBlankRow(), id: r.id } : r) };
        }));
      },
    });
  }, [currentSelId, masterProduct, recordRemovedApiId]);

  const toggleDiscountType = (type) => {
    updateSel(currentSelId, { discountType: type });
    updateRows(currentSelId, rows =>
      rows.map(r => calculateRow(r, 'discountType', type, gstType, type))
    );
  };

  const applyDefaults = (product) => {
    const qty = product.quantity !== undefined ? product.quantity : defaults.qty;
    const discPct = product.discountPercent !== undefined ? product.discountPercent : defaults.discountPercent;
    const discPriceExplicit = product.discountPrice !== undefined ? product.discountPrice : 0;

    const discType = currentSel.discountType;
    const priceBase = gstType === 'INC'
      ? Number(product.mrp) || 0
      : Number(product.basePrice) || 0;

    if (discPriceExplicit > 0) {
      return calculateRow(
        { ...makeBlankRow(), ...product, id: genId(), quantity: qty, discountPrice: discPriceExplicit },
        'discountPrice', discPriceExplicit, gstType, discType
      );
    }

    if (discType === 'price') {
      const discountPrice = R2(priceBase * discPct / 100);
      return calculateRow(
        { ...makeBlankRow(), ...product, id: genId(), quantity: qty, discountPrice },
        'discountPrice', discountPrice, gstType, discType
      );
    }

    return calculateRow(
      { ...makeBlankRow(), ...product, id: genId(), quantity: qty, discountPercent: discPct, discountPrice: 0 },
      'discountPercent', discPct, gstType, discType
    );
  };

  const commitAddProductsToCurrentSel = useCallback((products, applyFn = applyDefaults, forceAdd = false) => {
    let warnings = [];
    setSelectionsDirty(prev => {
      const next = [...prev];
      const sel = { ...next[currentSelId - 1] };
      const existingCodes = sel.rows.map(r => r.productCode).filter(Boolean);
      const remaining = MAX_SKUS_PER_SELECTION - existingCodes.length;

      let eligibleRows = forceAdd ? products : products.filter(p => !existingCodes.includes(p.productCode));

      if (eligibleRows.length > remaining) {
        warnings.push(`Added first ${remaining} products. ${eligibleRows.length - remaining} products were skipped (limit: ${MAX_SKUS_PER_SELECTION}).`);
        eligibleRows = eligibleRows.slice(0, remaining);
      }

      const newRows = eligibleRows.map(applyDefaults);
      const isOnlyBlank = sel.rows.length === 1 && !sel.rows[0].productCode;

      next[currentSelId - 1] = {
        ...sel,
        rows: isOnlyBlank ? (newRows.length > 0 ? newRows : [makeBlankRow()]) : [...sel.rows, ...newRows],
      };

      return next;
    });

    if (warnings.length > 0) {
      setTimeout(() => {
        const uniqueWarnings = Array.from(new Set(warnings));
        if (uniqueWarnings.length > 0) {
          toast.warn(`Failed to match: ${uniqueWarnings.join(', ')}`);
        }
      }, 10);
    }
  }, [currentSel, currentSelId, applyDefaults]);

  const commitAiMultiSelections = useCallback((payload) => {
    let warnings = [];
    let firstTargetIndex = -1;

    setSelectionsDirty(prev => {
      const next = [...prev];
      payload.forEach(({ selectionIndex, selectionName, isNewSelection, products }) => {
        let targetIndex;

        const safeSelectionName = String(selectionName || '').toLowerCase().trim();
        const existingByNameIndex = safeSelectionName
          ? next.findIndex(s => String(s.name || '').toLowerCase().trim() === safeSelectionName)
          : -1;

        if (existingByNameIndex !== -1 && !isNewSelection) {
          targetIndex = existingByNameIndex;
        }
        else if (isNewSelection || (selectionIndex && selectionIndex > 0) || (safeSelectionName && existingByNameIndex === -1 && safeSelectionName !== String(next[currentSelId - 1]?.name || '').toLowerCase().trim())) {
          targetIndex = (selectionIndex && selectionIndex > 0) ? selectionIndex - 1 : next.length;

          if (targetIndex >= 15) {
            warnings.push('Maximum 15 selections allowed. Adding to Selection 15 instead.');
            targetIndex = 14;
          }

          while (targetIndex >= next.length) {
            const newId = next.length + 1;
            next.push({ id: newId, name: `Selection ${newId}`, rows: [], discountType: 'price', manualDiscountAmount: 0, overridePriceAmount: 0 });
          }
        }
        else {
          targetIndex = currentSelId - 1;
        }

        next[targetIndex] = { ...next[targetIndex] };
        const sel = next[targetIndex];

        const existingCodes = sel.rows.map(r => r.productCode).filter(Boolean);
        const remaining = MAX_SKUS_PER_SELECTION - existingCodes.length;

        let eligibleRows = products;

        if (eligibleRows.length > remaining) {
          warnings.push(`Added first ${remaining} products to "${sel.name}". ${eligibleRows.length - remaining} products were skipped (limit: ${MAX_SKUS_PER_SELECTION}).`);
          eligibleRows = eligibleRows.slice(0, remaining);
        }

        let newRows = [];
        let rowsToRemove = [];

        eligibleRows.forEach(p => {
          if (p.actionType === 'remove') {
            rowsToRemove.push(p);
          } else {
            const qty = p.quantity !== undefined ? p.quantity : 1;
            const dPct = p.discountPercent || 0;
            const dPri = p.discountPrice || 0;

            let row = { ...makeBlankRow(), ...p, id: genId(), quantity: qty };

            if (dPri > 0) {
              row = calculateRow(row, 'discountPrice', dPri, gstType, 'price');
            } else {
              row = calculateRow(row, 'discountPercent', dPct, gstType, 'percent');
            }
            newRows.push(row);
          }
        });

        let currentRows = sel.rows;
        if (rowsToRemove.length > 0) {
          const removeCodes = rowsToRemove.map(r => r.productCode).filter(Boolean);
          currentRows = currentRows.filter(r => !removeCodes.includes(r.productCode));
          if (currentRows.length === 0) currentRows = [makeBlankRow()];
        }

        const isOnlyBlank = currentRows.length === 1 && !currentRows[0].productCode;
        next[targetIndex] = {
          ...sel,
          name: selectionName ? selectionName : sel.name,
          rows: isOnlyBlank ? (newRows.length > 0 ? newRows : [makeBlankRow()]) : [...currentRows, ...newRows],
        };
        if (firstTargetIndex === -1) {
          firstTargetIndex = targetIndex;
        }
      });
      return next;
    });

    if (warnings.length > 0) {
      setTimeout(() => {
        const uniqueWarnings = Array.from(new Set(warnings));
        if (uniqueWarnings.length > 0) {
          toast.warn(`Failed to match: ${uniqueWarnings.join(', ')}`);
        }
      }, 10);
    }

    if (firstTargetIndex !== -1) {
      setTimeout(() => {
        setCurrentSelId(firstTargetIndex + 1);
      }, 50);
    }
  }, [currentSelId, gstType]);

  const commitExcelImport = useCallback((payload) => {
    let warnings = [];
    setSelectionsDirty(prev => {
      const next = [...prev];
      payload.forEach(({ selectionIndex, rows }) => {
        if (selectionIndex < 0 || selectionIndex >= next.length) return;
        const sel = next[selectionIndex];
        const discType = sel.discountType || 'percent';
        const existingCodes = sel.rows.map(r => r.productCode).filter(Boolean);
        const currentCount = existingCodes.length;
        const remaining = MAX_SKUS_PER_SELECTION - currentCount;

        let eligibleRows = rows.filter(p => !existingCodes.includes(p.productCode));

        if (eligibleRows.length > remaining) {
          warnings.push(`Added first ${remaining} products into "${sel.name}". ${eligibleRows.length - remaining} products were skipped (limit: ${MAX_SKUS_PER_SELECTION}).`);
          eligibleRows = eligibleRows.slice(0, remaining);
        }

        const newRows = eligibleRows.map(p => calculateRow(
          { ...makeBlankRow(), ...p, id: genId() },
          'discountPercent', p.discountPercent || 0, gstType, discType
        ));

        const isOnlyBlank = sel.rows.length === 1 && !sel.rows[0].productCode;
        next[selectionIndex] = {
          ...sel,
          rows: isOnlyBlank ? (newRows.length > 0 ? newRows : [makeBlankRow()]) : [...sel.rows, ...newRows],
        };
      });
      return next;
    });

    if (warnings.length > 0) {
      setTimeout(() => {
        const uniqueWarnings = Array.from(new Set(warnings));
        if (uniqueWarnings.length > 0) {
          toast.warn(`Failed to match: ${uniqueWarnings.join(', ')}`);
        }
      }, 10);
    }
  }, [gstType]);


  // const commitCopyToSelections = useCallback((sourceIds, targetIds, mode) => {
  //   setSelectionsDirty(prev => {
  //     // gather all rows from every source selection up front
  //     const srcRowsBySrc = {};
  //     sourceIds.forEach(srcId => {
  //       const srcSel = prev.find(s => s.id === srcId);
  //       srcRowsBySrc[srcId] = srcSel ? srcSel.rows.filter(r => r.productCode) : [];
  //     });

  //     return prev.map(sel => {
  //       if (!targetIds.includes(sel.id)) return sel;

  //       const base = mode === 'replace'
  //         ? []
  //         : (sel.rows.length === 1 && !sel.rows[0].productCode ? [] : sel.rows);

  //       let existingCodes = base.map(r => r.productCode);
  //       let remaining = MAX_SKUS_PER_SELECTION - existingCodes.length;

  //       let freshRows = [];
  //       sourceIds.forEach(srcId => {
  //         if (remaining <= 0) return;
  //         const filtered = srcRowsBySrc[srcId].filter(
  //           r => !existingCodes.includes(r.productCode)
  //         );
  //         const toAdd = filtered.slice(0, remaining).map(r => ({
  //           ...r,
  //           id: genId(),
  //           apiId: null,
  //           copyId: `${srcId}-${sel.id}-${r.id}`,
  //         }));
  //         freshRows = [...freshRows, ...toAdd];
  //         existingCodes = [...existingCodes, ...toAdd.map(r => r.productCode)];
  //         remaining -= toAdd.length;
  //       });

  //       return {
  //         ...sel,
  //         rows: base.length === 0 && freshRows.length === 0 ? [makeBlankRow()] : [...base, ...freshRows],
  //       };
  //     });
  //   });
  // }, []);

  const commitCopyToSelections = useCallback((sourceIds, targetIds, mode, selectedProducts) => {
    setSelectionsDirty(prev => {
      // gather only the chosen rows from every source selection up front
      const srcRowsBySrc = {};
      sourceIds.forEach(srcId => {
        srcRowsBySrc[srcId] = pickRowsForCopy(prev, srcId, selectedProducts);
      });

      return prev.map(sel => {
        if (!targetIds.includes(sel.id)) return sel;

        const base = mode === 'replace'
          ? []
          : (sel.rows.length === 1 && !sel.rows[0].productCode ? [] : sel.rows);

        let existingCodes = base.map(r => r.productCode);
        let remaining = MAX_SKUS_PER_SELECTION - existingCodes.length;

        let freshRows = [];
        sourceIds.forEach(srcId => {
          if (remaining <= 0) return;
          const filtered = srcRowsBySrc[srcId].filter(r => !existingCodes.includes(r.productCode));
          const toAdd = filtered.slice(0, remaining).map(r => ({
            ...r,
            id: genId(),
            apiId: null,
            copyId: `${srcId}-${sel.id}-${r.id}`,
          }));
          freshRows = [...freshRows, ...toAdd];
          existingCodes = [...existingCodes, ...toAdd.map(r => r.productCode)];
          remaining -= toAdd.length;
        });

        return {
          ...sel,
          rows: base.length === 0 && freshRows.length === 0 ? [makeBlankRow()] : [...base, ...freshRows],
        };
      });
    });
  }, []);


  const handleUndoCopy = useCallback((targetIds, srcSelId) => {
    setSelectionsDirty(prev => prev.map(sel => {
      if (!targetIds.includes(sel.id)) return sel;

      sel.rows.forEach(r => {
        if (r.copyId?.startsWith(`${srcSelId}-`) && r.apiId) {
          recordRemovedApiId(sel.id, r.apiId);
        }
      });

      const kept = sel.rows.filter(
        r => !r.copyId || !r.copyId.startsWith(`${srcSelId}-`)
      );

      return {
        ...sel,
        rows: kept.length === 0 ? [makeBlankRow()] : kept,
      };
    }));
  }, [recordRemovedApiId]);

  const addProductsToCurrentSel = useCallback((products, forceAdd = false) => {
    const arr = Array.isArray(products) ? products : [products];

    if (forceAdd) {
      commitAddProductsToCurrentSel(arr, applyDefaults, true);
      return;
    }

    const skus = arr.map(p => p.productCode || p.sku_code || '');

    const zeroSkus = getZeroStockSkus(masterProduct, skus);

    if (zeroSkus.length === 0) {
      commitAddProductsToCurrentSel(arr, applyDefaults, forceAdd);
      return;
    }

    const zeroProducts = zeroSkus.map(sku => {
      const m = masterProduct.find(p => p.sku_code === sku);
      const original = arr.find(p => (p.productCode || p.sku_code) === sku) || {};
      return {
        ...original,
        productCode: sku,
        brand: m?.brand || '',
        description: m?.product_desc || m?.description || '',
        mrp: m?.mrp || 0,
        image: m?.image || null,
      };
    });

    handleStockConfirm({
      products: zeroProducts,
      totalCount: arr.length,
      onConfirm: () => {
        commitAddProductsToCurrentSel(arr, applyDefaults, forceAdd);
        setActiveSkuForSuggestion(arr[arr.length - 1]?.productCode || arr[arr.length - 1]?.sku_code);
      },
      onCancel: null,
    });
  }, [masterProduct, commitAddProductsToCurrentSel]);

  const handleAddFromSearchModal = useCallback((products) => {
    const arr = Array.isArray(products) ? products : [products];
    const skus = arr.map(p => p.productCode || p.sku_code || '');
    const zeroSkus = getZeroStockSkus(masterProduct, skus);

    const applyModalValues = (p) => {
      const discType = currentSel.discountType;
      const priceBase = gstType === 'INC'
        ? Number(p.mrp) || 0
        : Number(p.basePrice) || 0;

      if (discType === 'price') {
        const discountPrice = R2(priceBase * (Number(p.discountPercent) || 0) / 100);
        return calculateRow(
          { ...makeBlankRow(), ...p, id: genId(), discountPrice },
          'discountPrice', discountPrice, gstType, discType
        );
      }

      return calculateRow(
        { ...makeBlankRow(), ...p, id: genId() },
        'discountPercent', Number(p.discountPercent) || 0, gstType, discType
      );
    };

    if (zeroSkus.length === 0) {
      commitAddProductsToCurrentSel(arr, applyModalValues);
      return;
    }

    const zeroProducts = zeroSkus.map(sku => {
      const m = masterProduct.find(p => p.sku_code === sku);
      return {
        productCode: sku,
        brand: m?.brand || '',
        description: m?.product_desc || m?.description || '',
        mrp: m?.mrp || 0,
        image: m?.image || null,
      };
    });

    handleStockConfirm({
      products: zeroProducts,
      totalCount: arr.length,
      onConfirm: () => {
        commitAddProductsToCurrentSel(arr, applyModalValues);
        setActiveSkuForSuggestion(arr[arr.length - 1]?.productCode || arr[arr.length - 1]?.sku_code);
      },
      onCancel: null,
    });
  }, [masterProduct, commitAddProductsToCurrentSel, gstType, currentSel.discountType]);

  const handleAiMultiSelections = useCallback(async (payload, aiWarnings = [], extractedClientName = null, isDirectAdd = false) => {
    const enrichedPayload = payload.map(sel => {
      const enrichedProducts = sel.products.map(p => {
        const sku = p.productCode || p.sku_code || '';
        const m = masterProduct.find(mp => mp.sku_code === sku);
        return {
          ...p,
          productCode: sku,
          brand: m?.brand || '',
          description: m?.product_desc || m?.description || '',
          mrp: m?.mrp || 0,
          image: m?.image || null,
          linediagram_image: m.linediagram_image || null,
          stockAvailable: m?.stockAvailable || 0,
          collection: m?.collection || '',
          preferences: m?.preferences || '',
          productType: m?.product_typ_group || '',
          category: m?.category || '',
          priceSegment: m?.priceSegment || '',
          finish: m?.finish || '',
          pruning_date: m?.pruning_date || '',
          status: m?.status || ''
        };
      });
      return { ...sel, products: enrichedProducts };
    });

    if (extractedClientName) {
      const safeClientName = String(extractedClientName || '').toLowerCase();
      const matchedClient = clientOpt.find(c =>
        c.client_name && String(c.client_name || '').toLowerCase().includes(safeClientName)
      );
      if (matchedClient) {
        setSelectedClient(matchedClient);
        setAiChatHistory(prev => [...prev, { id: Date.now() + 'c_succ', sender: 'ai', text: `Client "${matchedClient.client_name}" automatically selected!`, isSuccess: true }]);
      } else {
        setAiChatHistory(prev => [...prev, { id: Date.now() + 'c_err', sender: 'ai', text: `Client "${extractedClientName}" not found in your account.`, isError: true }]);
      }
    }

    if (isDirectAdd) {
      commitAiMultiSelections(enrichedPayload);
    }
  }, [masterProduct, clientOpt, commitAiMultiSelections]);



  useEffect(() => {
    if (location.state?.aiPendingPayload) {
      commitAiMultiSelections(location.state.aiPendingPayload);

      const stateCopy = { ...location.state };
      delete stateCopy.aiPendingPayload;
      navigate(location.pathname, { state: stateCopy, replace: true });
    }
  }, [location.state, commitAiMultiSelections, navigate]);

  const handleExcelImport = useCallback((payload) => {
    const allSkus = payload.flatMap(({ rows }) => rows.map(r => r.productCode || r.sku_code || ''));

    const zeroSkus = getZeroStockSkus(masterProduct, allSkus);

    if (zeroSkus.length === 0) {
      commitExcelImport(payload);
      return;
    }

    const zeroProducts = zeroSkus.map(sku => {
      const m = masterProduct.find(p => p.sku_code === sku);
      return {
        productCode: sku,
        brand: m?.brand || '',
        description: m?.product_desc || m?.description || '',
        mrp: m?.mrp || 0,
        image: m?.image || null,
      };
    });

    handleStockConfirm({
      products: zeroProducts,
      totalCount: allSkus.filter(Boolean).length,
      onConfirm: () => commitExcelImport(payload),
      onCancel: null,
    });
  }, [masterProduct, commitExcelImport]);


  // const handleCopyToSelections = useCallback((sourceIds, targetIds, mode) => {
  //   const allSrcRows = sourceIds.flatMap(id =>
  //     (selections.find(s => s.id === id)?.rows || []).filter(r => r.productCode)
  //   );
  //   const skus = allSrcRows.map(r => r.productCode);
  //   const zeroSkus = getZeroStockSkus(masterProduct, skus);

  //   if (zeroSkus.length === 0) {
  //     commitCopyToSelections(sourceIds, targetIds, mode);
  //     return;
  //   }

  //   const zeroProducts = zeroSkus.map(sku => {
  //     const m = masterProduct.find(p => p.sku_code === sku);
  //     return {
  //       productCode: sku,
  //       brand: m?.brand || '',
  //       description: m?.product_desc || m?.description || '',
  //       mrp: m?.mrp || 0,
  //       image: m?.image || null,
  //     };
  //   });

  //   handleStockConfirm({
  //     products: zeroProducts,
  //     totalCount: allSrcRows.length,
  //     onConfirm: () => commitCopyToSelections(sourceIds, targetIds, mode),
  //     onCancel: null,
  //   });
  // }, [selections, masterProduct, commitCopyToSelections, handleStockConfirm]);

  const handleCopyToSelections = useCallback((sourceIds, targetIds, mode, selectedProducts) => {
    const allSrcRows = sourceIds.flatMap(id => pickRowsForCopy(selections, id, selectedProducts));
    const skus = [...new Set(allSrcRows.map(r => r.productCode))];
    const zeroSkus = getZeroStockSkus(masterProduct, skus);

    if (zeroSkus.length === 0) {
      commitCopyToSelections(sourceIds, targetIds, mode, selectedProducts);
      return;
    }

    const zeroProducts = zeroSkus.map(sku => {
      const m = masterProduct.find(p => p.sku_code === sku);
      return {
        productCode: sku,
        brand: m?.brand || '',
        description: m?.product_desc || m?.description || '',
        mrp: m?.mrp || 0,
        image: m?.image || null,
      };
    });

    handleStockConfirm({
      products: zeroProducts,
      totalCount: skus.length,
      onConfirm: () => commitCopyToSelections(sourceIds, targetIds, mode, selectedProducts),
      onCancel: null,
    });
  }, [selections, masterProduct, commitCopyToSelections, handleStockConfirm]);

  const getDraftPayload = useCallback(() => {
    const tableData = [];
    const clientNameClean = selectedClient?.client_name
      ? selectedClient.client_name.replace(/[\s_]/g, '') : 'Unknown';
    const generatedProjectId = `P-${clientNameClean}${Date.now().toString().slice(-6)}`;
    const allRemovedIds = Object.values(removedApiIdsRef.current)
      .flatMap(set => Array.from(set));

    selections.forEach((sel) => {
      applyRowSorting(sel.rows, sel.sorting).forEach((row) => {
        if (!row.productCode) return;
        tableData.push({
          id: row.apiId ?? null,
          copy_id: null,
          sku_code: row.productCode,
          quantity: Number(row.quantity) || 0,
          area: row.area || '',
          mrp: Number(row.mrp) || 0,
          discount_price: Number(row.discountPrice) || 0,
          discount_per: Number(row.discountPercent) || 0,
          gst: gstType === 'EXC' ? 0 : (Number(row.gstRate) || 0),
          total: String(row.total || 0),
          discount: Number(row.totalDiscount) || 0,
          selection: String(sel.id),
          selection_name: sel.name,
          unit: Number(row.basePrice) || 0,
        });
      });
    });
    return {
      quatationId: generatedProjectId,
      projectId: draftId,
      tableData,
      selectionClient: selectedClient?.id || null,
      selectedDate: validUpto || null,
      removedIds: allRemovedIds,
      gstType,
      amountType: 'price',
      selectionHistoryForEdit: visitedSelIds,
    };
  }, [selections, selectedClient, validUpto, gstType, visitedSelIds, draftId]);

  getPayloadRef.current = getDraftPayload;

  const handleReset = async () => {
    const result = await Swal.fire({
      title: 'Start Fresh?',
      html: '<p style="color: #64748b; font-size: 14px; margin-top: 5px;">This will clear all rows, client details, and settings from this page. This action cannot be undone.</p>',
      icon: 'warning',
      iconColor: '#ef4444',
      showCancelButton: true,
      confirmButtonText: 'Yes, Reset',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      background: '#ffffff',
      backdrop: 'rgba(15, 23, 42, 0.5)',
      customClass: {
        popup: 'rounded-2xl shadow-2xl border border-slate-100 pb-2',
        title: 'text-xl font-bold text-slate-800 pt-2',
        confirmButton: 'bg-red-500 text-white rounded-xl px-6 py-2.5 font-semibold shadow-lg shadow-red-500/30 hover:bg-red-600 transition-all mx-2',
        cancelButton: 'bg-slate-100 text-slate-600 rounded-xl px-6 py-2.5 font-semibold hover:bg-slate-200 transition-all mx-2'
      }
    });

    if (result.isConfirmed) {
      resetAfterSave();
      setTempName('');
      dispatch(resetAiState());
      try {
        sessionStorage.removeItem('unsaved_quote_state');
      } catch (e) { }
      navigate('/user/create-quote', { replace: true });
    }
  };

  const grandTotal = R2(
    selections.reduce((sum, sel) => {
      return sum + sel.rows.reduce((s, r) => s + (r.total || 0), 0);
    }, 0)
  );
  const itemCount = currentSel.rows.filter(r => r.productCode).length;
  const isCurrentLoading = loadingSelIds.has(currentSelId);
  const currentSelHasColumnSort = (currentSel?.sorting?.length ?? 0) > 0;

  const handleReorder = useCallback((newRows) => {
    const selId = currentSelId;
    setSelectionsDirty(prev => prev.map(s => {
      if (s.id !== selId) return s;
      return {
        ...s,
        rows: newRows,
        sorting: [],
      };
    }));
  }, [currentSelId, setSelectionsDirty]);

  const formattedHeadingId = (() => {
    const id = quoteDetails?.project_id;
    if (!id) return editProjectId || paramProjectId;
    const vMatch = id.match(/(_v\d+)$/);
    const version = vMatch ? vMatch[1] : '';
    const baseId = id.replace(/(_v\d+)$/, '');
    const lastFour = baseId.slice(-4);
    return `${lastFour}${version}`;
  })();

  return (
    <div className={`flex -m-2 flex-col overflow-hidden bg-transparent transition-all duration-300 ease-in-out no-scrollbar [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]`}
      style={{ height: 'calc(100vh - 56px)', fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      <UserTopbar
        heading={isEditMode ? `Edit Quotation — ${formattedHeadingId}` : 'Create Quotation'}
        sidebar={isSidebarOpen}
      />

      {!isOnline && (
        <div className="bg-red-50 text-red-700 px-4 py-2 text-[12px] font-bold flex items-center justify-center gap-2 border-b border-red-200 z-30 shadow-sm relative shrink-0 transition-all">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          You are currently offline. Auto-save is paused. Please check your internet connection.
        </div>
      )}

      <div className="flex-none z-20" style={{ background: '#fff', borderBottom: '1.5px solid #e8ecf4', fontFamily: "'Inter', -apple-system, sans-serif" }}>

        <div className="px-[18px] py-2.5 flex items-center gap-1.5 flex-wrap"
          style={{ borderBottom: '1px solid #f0f3fa', background: 'linear-gradient(to bottom, #fff, #fafbfd)' }}>

          <button onClick={() => setIsClientOpen(true)}
            disabled={editProjectId && !clientOpt?.some(client => client.id == (cid || selectedClient?.id))}
            className="flex items-center gap-1.5 px-3 py-[5px] text-[11px] font-semibold rounded-lg transition-all disabled:opacity-40"
            style={selectedClient
              ? { background: 'linear-gradient(135deg,#f0fdf8,#e6faf3)', color: '#0d7a5a', border: '1.5px solid #6ee7b7', fontWeight: 700 }
              : { background: '#fff', color: '#4a5568', border: '1.5px solid #e2e8f0' }}>
            <User size={12} />
            {selectedClient ? selectedClient.client_name : 'Select Client'}
          </button>

          <div className="w-px h-[18px] mx-0.5" style={{ background: '#e2e8f0' }} />

          <div className="flex items-center gap-1.5">
            <div className="relative">
              <select
                value={currentSelId}
                onChange={e => switchSelection(e.target.value)}
                className="appearance-none pl-2.5 pr-7 py-[5px] text-[11px] font-semibold rounded-lg focus:outline-none cursor-pointer transition-all"
                style={{ minWidth: 60, height: 30, background: '#fff', color: '#2d3748', border: '1.5px solid #e2e8f0', fontFamily: 'inherit' }}
              >
                {selections.map(sel => {
                  const cnt = sel.rows.filter(r => r.productCode).length;
                  const isLoading = loadingSelIds.has(sel.id);
                  return (
                    <option key={sel.id} value={sel.id}>
                      {isLoading ? `⏳ S${sel.id}` : `S${sel.id}${cnt > 0 ? ` (${cnt})` : ''}`}
                    </option>
                  );
                })}
              </select>
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[9px]" style={{ color: '#94a3b8' }}>▾</span>
            </div>

            <input
              ref={nameInputRef}
              value={editingName === currentSelId ? tempName : currentSel.name}
              onFocus={() => {
                setEditingName(currentSelId);
                setTempName(currentSel.name);
              }}
              onChange={e => {
                const sanitizedValue = e.target.value
                  .replace(/[*?:\\\/\[\]]/g, '')
                  .slice(0, MAX_SELECTION_NAME_LENGTH);
                setTempName(sanitizedValue);
              }}
              onBlur={commitRename}
              onKeyDown={e => {
                if (e.key === 'Enter') { commitRename(); e.target.blur(); }
                if (e.key === 'Escape') { setEditingName(null); e.target.blur(); }
              }}
              placeholder="Selection name…"
              className="text-[11px] font-semibold rounded-lg px-2.5 py-[5px] w-40 transition-all focus:outline-none"
              style={{
                background: editingName === currentSelId ? '#fff' : '#f8fafc',
                color: '#1e293b',
                height: 30,
                border: editingName === currentSelId
                  ? '1.5px solid #1a56db'
                  : '1.5px solid #e2e8f0',
                boxShadow: editingName === currentSelId
                  ? '0 0 0 3px rgba(26,86,219,.1)'
                  : 'none',
                fontFamily: 'inherit',
              }}
            />
          </div>

          <button onClick={() => setIsCopyOpen(true)}
            className="flex items-center gap-1.5 px-2.5 py-[5px] text-[11px] font-semibold rounded-lg transition-all"
            style={{ background: '#fff', color: '#4a5568', border: '1.5px solid #e2e8f0' }}>
            <Copy size={11} /> Copy
          </button>

          <div className="w-px h-[18px] mx-0.5" style={{ background: '#e2e8f0' }} />

          <span className="text-[9.5px] font-bold uppercase tracking-[.07em]" style={{ color: '#9ba8be' }}>GST</span>
          <div className="flex rounded-lg overflow-hidden" style={{ border: '1.5px solid #e2e8f0', background: '#f7f9fc' }}>
            {['INC', 'EXC'].map(t => (
              <button key={t} onClick={() => toggleGstType(t)}
                className={`px-3 py-[5px] text-[11px] font-bold transition-all rounded-md ${gstType === t ? 'ring-2 ring-[#ff7b00] ring-offset-1 animate-pulse' : ''
                  }`}
                style={gstType === t
                  ? { background: '#ff7b00', color: '#fff', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.15)' }
                  : { background: 'transparent', color: '#94a3b8' }}>
                {t === 'INC' ? 'Incl.' : 'Excl.'}
              </button>
            ))}
          </div>

          <div className="w-px h-[18px] mx-0.5" style={{ background: '#e2e8f0' }} />

          <span className="text-[9.5px] font-bold uppercase tracking-[.07em]" style={{ color: '#9ba8be' }}>Disc</span>
          <div className="flex rounded-lg overflow-hidden" style={{ border: '1.5px solid #e2e8f0', background: '#f7f9fc' }}>
            {[['percent', 'By %'], ['price', `By ${CURRENCY_SYMBOLS[getSelectedCurrency()] || '₹'}`]].map(([val, label]) => (
              <button key={val} onClick={() => toggleDiscountType(val)}
                className={`px-3 py-[5px] text-[11px] font-bold transition-all rounded-md ${currentSel.discountType === val ? 'ring-2 ring-[#ff7b00] ring-offset-1 animate-pulse' : ''
                  }`}
                style={currentSel.discountType === val
                  ? { background: '#ff7b00', color: '#fff', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.15)' }
                  : { background: 'transparent', color: '#94a3b8' }}>
                {label}
              </button>
            ))}
          </div>

          <div className="w-px h-[18px] mx-0.5" style={{ background: '#e2e8f0' }} />

          <span className="text-[9.5px] font-bold uppercase tracking-[.07em] whitespace-nowrap" style={{ color: '#9ba8be' }}>Valid Upto</span>
          <input type="date" value={validUpto} onChange={e => { setValidUpto(e.target.value); isDirtyRef.current = true; }}
            className="text-[11px] font-semibold rounded-lg px-2.5 py-[5px] focus:outline-none cursor-pointer transition-all"
            style={{ background: '#fff', color: '#2d3748', border: '1.5px solid #e2e8f0', fontFamily: 'inherit' }} />

          <div className="w-px h-[18px] mx-0.5" style={{ background: '#e2e8f0' }} />

          <button onClick={() => setIsSearchOpen(true)}>
            <SmartSearchButton />
          </button>

          <div className="w-px h-[18px] mx-0.5" style={{ background: '#e2e8f0' }} />

          <button
            onClick={() => {
              const hasProducts = selections.some(sel =>
                sel.rows.some(r => r.productCode && r.productCode.trim() !== '')
              );
              if (hasProducts) {
                toast.warn('Cannot import Excel when selections already have products. Please reset all selections first.');
                return;
              }
              setIsExcelOpen(true);
            }}
            className="flex items-center gap-1.5 px-2.5 py-[5px] text-[11px] font-bold rounded-lg transition-all"
            style={{ background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', color: '#15803d', border: '1.5px solid #86efac' }}>
            <FileSpreadsheet size={12} /> Template
          </button>

          <div className="w-px h-[18px] mx-0.5" style={{ background: '#e2e8f0' }} />

          <DefaultsPopover defaults={defaults} onChange={setDefaults} />
        </div>

        <div className="px-[18px] py-2 flex items-center gap-1.5" style={{ borderBottom: '1px solid #f0f3fa', background: '#fff' }}>

          <button onClick={handleReset}
            className="flex items-center gap-1.5 px-2.5 py-[5px] text-[11px] font-semibold rounded-lg transition-all"
            style={{ background: '#fef2f2', color: '#dc2626', border: '1.5px solid #fecaca' }}>
            <RotateCcw size={11} /> Reset
          </button>

          {/* <button onClick={() => {
            const currentCount = currentSel.rows.filter(r => r.productCode).length;
            if (currentCount >= MAX_SKUS_PER_SELECTION) {
              toast.warn(`Maximum ${MAX_SKUS_PER_SELECTION} products allowed per selection.`);
              return;
            }
            updateRows(currentSelId, rows => [...rows, makeBlankRow()]);
          }}
            className="flex items-center gap-1.5 px-2.5 py-[5px] text-[11px] font-semibold rounded-lg transition-all"
            style={{ background: '#f8fafc', color: '#4a5568', border: '1.5px solid #dde3ef' }}>
            <Plus size={11} /> Add Row
          </button> */}

          {/* <button
            onClick={handleSmartSort}
            title={
              currentSel.rows.some(r => (r.area || '').trim() !== '')
                ? 'Sort by Brand → Area (empty areas last)'
                : 'Sort by Brand + Price Segment'
            }
            className="flex items-center gap-1.5 px-2.5 py-[5px] text-[11px] font-bold rounded-lg transition-all"
            style={{ background: 'linear-gradient(135deg,#eff6ff,#dbeafe)', color: '#1e40af', border: '1.5px solid #93c5fd' }}>
            <ArrowDownUp size={11} /> Smart Sort
          </button> */}



          <div className="relative flex items-center w-[160px]">
            <input
              ref={addSkuInputRef}
              type="text"
              placeholder="Add SKU & press Enter"
              maxLength={25}
              className={`w-full text-[11px] py-[3px] pl-2 pr-6 rounded border bg-white outline-none transition-all shadow-inner placeholder:text-slate-400 font-medium text-slate-700 ${isCatalogReady ? 'border-green-500 focus:ring-1 focus:ring-green-500' : 'border-red-500 focus:ring-1 focus:ring-red-500'}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.nativeEvent) {
                    e.nativeEvent.stopImmediatePropagation();
                  }

                  const skuInput = e.target.value.trim();
                  if (skuInput) {
                    const skuLower = skuInput.toLowerCase();
                    const m = skuMap.get(skuLower);
                    if (m) {
                      const existingCodes = currentSel.rows.map(r => r.productCode).filter(Boolean);
                      const isDuplicate = existingCodes.includes(m.sku_code);

                      const mappedProduct = {
                        productCode: m.sku_code || '',
                        brand: m.brand || '',
                        description: m.product_desc || '',
                        collection: m.collection || '',
                        preferences: m.preferences || '',
                        pruning_date: m.pruning_date || '',
                        status: m.status || '',
                        productType: m.product_typ_group || '',
                        category: m.category || '',
                        priceSegment: m.priceSegment || '',
                        finish: m.finish || '',
                        image: m.image || null,
                        linediagram_image: m.linediagram_image || null,
                        mrp: Number(m.mrp) || 0,
                        basePrice: Number(m.basePrice) || 0,
                        gstRate: Number(m.gst) || 18,
                        stock: Number(m.stockAvailable) || 0,
                        stockIndicator: m.stockIndicator || '',
                        quantity: defaults?.qty || 1,
                        discountPercent: defaults?.discountPercent || 0,
                        discountPrice: 0,
                      };

                      handleStockConfirm({
                        products: [mappedProduct],
                        totalCount: 1,
                        mode: isDuplicate ? 'duplicate' : 'unique',
                        onConfirm: () => {
                          addProductsToCurrentSel([mappedProduct], true);
                          setActiveSkuForSuggestion(m.sku_code);
                          if (addSkuInputRef.current) addSkuInputRef.current.focus();
                        },
                        onCancel: () => {
                          if (addSkuInputRef.current) addSkuInputRef.current.focus();
                        },
                      });
                      e.target.value = '';
                      e.target.blur();
                    } else {
                      toast.error(`SKU ${skuInput} not found`);
                    }
                  }
                }
              }}
            />
            <Plus size={12} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>

          <div className="flex-1" />

          {autoSaveStatus && (
            <span className="text-[10px] font-bold px-2.5 py-[5px] rounded-lg flex items-center gap-1.5 transition-all"
              style={autoSaveStatus === 'saving'
                ? { color: '#b45309', background: 'linear-gradient(135deg,#fffbeb,#fef3c7)', border: '1.5px solid #fcd34d' }
                : autoSaveStatus === 'saved'
                  ? { color: '#15803d', background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '1.5px solid #86efac' }
                  : { color: '#b91c1c', background: '#fef2f2', border: '1.5px solid #fca5a5' }}>
              {autoSaveStatus === 'saving' ? '⏳ Auto-saving…'
                : autoSaveStatus === 'saved' ? '✓ Auto-saved'
                  : '✕ Failed'}
            </span>
          )}

          <button onClick={() => setIsDraftOpen(true)}
            disabled={!isOnline || loadingSelIds.size > 0 || isDraftLoading}
            className="flex items-center gap-1.5 px-3 py-[5px] text-[11px] font-bold rounded-lg transition-all disabled:opacity-50"
            style={draftId
              ? { background: 'linear-gradient(135deg,#fffbeb,#fef3c7)', color: '#b45309', border: '1.5px solid #fcd34d' }
              : { background: '#fff', color: '#d97706', border: '1.5px solid #fde68a' }}>
            {draftId && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#d97706', display: 'inline-block', flexShrink: 0 }} />}
            {draftId ? `Draft ${draftUid}` : 'Save Draft'}
          </button>

          {isEditMode ? (
            <>
              <button onClick={() => saveQuote('new')}
                disabled={!isOnline || (editProjectId && !clientOpt?.some(client => client.id == (cid || selectedClient?.id))) || loadingSelIds.size > 0 || isDraftLoading}
                className="flex items-center gap-1.5 px-3 py-[5px] text-[11px] font-semibold rounded-lg transition-all disabled:opacity-40"
                style={{ background: '#f8fafc', color: '#4a5568', border: '1.5px solid #dde3ef' }}>
                Save as New
              </button>
              <button onClick={() => saveQuote('update')}
                disabled={!isOnline || loadingSelIds.size > 0 || isDraftLoading}
                className="flex items-center gap-2 px-4 py-[5px] text-[11px] font-bold rounded-lg transition-all disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#16a34a,#15803d)', color: '#fff', border: '1.5px solid #16a34a', boxShadow: '0 2px 8px rgba(22,163,74,.25),inset 0 1px 0 rgba(255,255,255,.15)' }}>
                <Save size={12} />
                Update Quote
                <span style={{ opacity: 0.6, fontWeight: 400, fontSize: 10 }}>#{quoteDetails?.project_id?.slice(0, 6)}</span>
              </button>
            </>
          ) : (
            <button onClick={() => saveQuote('new')}
              disabled={!isOnline || loadingSelIds.size > 0 || isDraftLoading}
              className="flex items-center gap-1.5 px-5 py-[5px] text-[11px] font-bold rounded-lg transition-all disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg,#16a34a,#15803d)', color: '#fff', border: '1.5px solid #16a34a', boxShadow: '0 2px 8px rgba(22,163,74,.25),inset 0 1px 0 rgba(255,255,255,.15)' }}>
              <Save size={12} /> Save Quote
            </button>
          )}
        </div>

        <div className="px-[18px] py-1.5 flex items-center gap-[5px] flex-wrap z-10 bg-white/70 backdrop-blur-md border-b border-slate-200/50 shadow-sm relative">

          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-0.5 rounded-full"
            style={{ background: '#eff6ff', color: '#1e40af', border: '1.5px solid #bfdbfe' }}>
            {currentSel.name}
          </span>

          {isCurrentLoading && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 animate-pulse"
              style={{ color: '#64748b', background: '#f7f9fc', border: '1.5px solid #dde3ef' }}>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400 animate-ping" /> Loading…
            </span>
          )}



          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{ color: '#475569', background: '#f7f9fc', border: '1.5px solid #dde3ef' }}>
            {itemCount} / {MAX_SKUS_PER_SELECTION} SKUs
          </span>

          {selectedClient && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1"
              style={{ color: '#15803d', background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '1.5px solid #86efac' }}>
              <User size={8} /> {selectedClient.client_name}
            </span>
          )}

          {validUpto && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{ color: '#b45309', background: 'linear-gradient(135deg,#fffbeb,#fef3c7)', border: '1.5px solid #fcd34d' }}>
              Valid {new Date(validUpto).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          )}

          {isEditMode && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{ color: '#475569', background: '#f7f9fc', border: '1.5px solid #dde3ef' }}>
              Editing {quoteDetails?.project_id || '---'}
            </span>
          )}

          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={gstType === 'INC'
              ? { color: '#475569', background: '#f7f9fc', border: '1.5px solid #dde3ef' }
              : { color: '#b45309', background: 'linear-gradient(135deg,#fffbeb,#fef3c7)', border: '1.5px solid #fcd34d' }}>
            GST {gstType === 'INC' ? 'Inclusive' : 'Exclusive'}
          </span>

          {(defaults.qty !== 1 || defaults.discountPercent !== 0) && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{ color: '#475569', background: '#f7f9fc', border: '1.5px solid #dde3ef' }}>
              Defaults: ×{defaults.qty}{defaults.discountPercent ? `, ${defaults.discountPercent}% off` : ''}
            </span>
          )}

          {currentSelHasColumnSort && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1"
              style={{ color: '#0369a1', background: 'linear-gradient(135deg,#f0f9ff,#e0f2fe)', border: '1.5px solid #7dd3fc' }}>
              <ArrowDownUp size={8} />
              {`Sorted: ${currentSel.sorting.map(s => s.id).join(', ')?.toUpperCase()}`}
            </span>
          )}

          <div className="flex-1" />

          <div className="flex items-baseline gap-1.5 px-3.5 py-1 rounded-[10px]"
            style={{ background: 'linear-gradient(135deg,#1a56db,#1e40af)', boxShadow: '0 2px 10px rgba(26,86,219,.25)' }}>
            <span className="text-[9.5px] font-bold uppercase tracking-[.06em]" style={{ color: 'rgba(255,255,255,.7)' }}>Gross Total</span>
            <span className="text-[14px] font-bold tabular-nums" style={{ color: '#fff', letterSpacing: '-.01em' }}>
              {/* ₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} */}
              {formatCurrency(grandTotal)}
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden relative">
        {isDraftLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3 bg-white border border-amber-200 rounded-2xl px-8 py-6 shadow-lg">
              <div className="w-8 h-8 rounded-full border-2 border-amber-200 border-t-amber-500 animate-spin" />
              <p className="text-[13px] font-semibold text-slate-600">Loading Draft {draftUid}…</p>
              <p className="text-[11px] text-slate-400">Please wait while we restore your draft</p>
            </div>
          </div>
        )}
        {isCurrentLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 rounded-full border-2 border-blue-200 border-t-blue-500 animate-spin" />
              <p className="text-[12px] font-semibold text-slate-500">Loading  {currentSel.name}…</p>
            </div>
          </div>
        )}

        <QuotationTable
          activeSkuForSuggestion={activeSkuForSuggestion}
          setActiveSkuForSuggestion={setActiveSkuForSuggestion}
          selections={selections}
          currentSelection={currentSel.id}
          data={currentSel.rows}
          onUpdate={handleUpdateRow}
          onDelete={handleDeleteRow}
          onInsertAfter={handleInsertAfter}
          onDeleteSelected={handleDeleteSelected}
          onProductCodeChange={handleProductCodeChange}
          discountType={currentSel.discountType}
          gstType={gstType}
          sorting={currentSel.sorting ?? []}
          onSortingChange={handleSelectionSortingChange}
          onSmartSort={handleSmartSort}
          onReorder={handleReorder}
          defaultQty={defaults.qty}
          defaultDiscountPercent={defaults.discountPercent}
          onAddProducts={addProductsToCurrentSel}
        />
      </div>

      <ProductSearchModal
        isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)}
        onAddProducts={handleAddFromSearchModal}
        existingIds={currentSel.rows.map(r => r.productCode).filter(Boolean)}
        defaultQty={defaults.qty} defaultDiscountPercent={defaults.discountPercent}
      />
      <CopyToSelectionModal
        isOpen={isCopyOpen} onClose={() => setIsCopyOpen(false)}
        selections={selections} currentSelId={currentSelId} onCopy={handleCopyToSelections} onUndoCopy={handleUndoCopy}
      />
      <ClientSelectModal
        isOpen={isClientOpen} onClose={() => setIsClientOpen(false)}
        selectedClient={selectedClient} onSelect={(client) => { isDirtyRef.current = true; setSelectedClient(client); }}
      />
      <ExcelImportModal
        isOpen={isExcelOpen} onClose={() => setIsExcelOpen(false)} onImport={handleExcelImport}
      />
      <DraftDialog
        isOpen={isDraftOpen} onClose={() => setIsDraftOpen(false)}
        draftId={draftId} onDraftId={(id) => { setDraftId(id); isDirtyRef.current = false; }} getPayload={getDraftPayload} setDraftId={setDraftId}
      />
      <SaveProgressModal
        isOpen={isSaveProgressOpen}
        onClose={handleSaveModalClose}
        items={saveProgressItems}
        projectId={
          savedProjectId && /_v\d+$/i.test(savedProjectId)
            ? savedProjectId.replace(/_v(\d+)$/i, (m, v) => `_v${parseInt(v, 10) + 1}`)
            : savedProjectId
        }
      />

      <StockConfirmationModal
        isOpen={Boolean(stockConfirm)}
        products={stockConfirm?.products ?? []}
        totalCount={stockConfirm?.totalCount ?? (stockConfirm?.products?.length ?? 0)}
        mode={stockConfirm?.mode || 'out_of_stock'}
        onClose={() => {
          stockConfirm?.onCancel?.();
          setStockConfirm(null);
        }}
        onConfirm={() => {
          stockConfirm?.onConfirm?.();
          setStockConfirm(null);
        }}
      />

      {/* <StockLable data={selections} currentSelection={currentSel.id} /> */}

      <AiPreviewModal
        isOpen={!!aiPreviewData}
        onClose={() => {
          setAiPreviewData(null);
          dispatch(setIsAiOpen(true));
        }}
        onConfirm={(finalPayload) => {
          if (finalPayload) {
            commitAiMultiSelections(finalPayload);
            setAiPreviewData(null);
          }
        }}
        payload={aiPreviewData?.payload || []}
        warnings={aiPreviewData?.warnings || []}
        clientName={aiPreviewData?.clientName}
      />

      {isAiEnabled && (
        <AiPromptModal
          isOpen={isAiOpen}
          onClose={() => dispatch(setIsAiOpen(false))}
          onOpen={() => dispatch(setIsAiOpen(true))}
          aiChatHistory={aiChatHistory}
          setAiChatHistory={(updater) => {
            const nextHistory = typeof updater === 'function' ? updater(aiChatHistory) : updater;
            dispatch(setAiChatHistory(nextHistory));
          }}
          apiKey={'AQ.Ab8RN6IOXbDWhG0K0L2OR9o_3mLuR7XGtsxaHYeG_E3fDvoG_Q'}
          masterProduct={masterProduct}
          clientOpt={clientOpt}
          selections={selections}
          activeSelectionIndex={selections.findIndex(s => s.id === currentSelId) + 1}
          onSelectClient={(client) => { isDirtyRef.current = true; setSelectedClient(client); }}
          onCopySelectionCommand={(srcIdx, tgtIdx, newName) => {
            isDirtyRef.current = true;
            setSelections(prev => {
              const next = [...prev];
              const safeSrcIdx = srcIdx !== null && srcIdx >= 0 && srcIdx < next.length ? srcIdx : next.findIndex(s => s.id === currentSelId);
              const srcSel = next[safeSrcIdx];
              if (!srcSel) return next;
              const newRows = srcSel.rows.map(r => ({
                ...r,
                id: genId(),
                apiId: null,
                copyId: `${currentSelId}-${srcSel.id}-${r.id}`
              }));
              if (tgtIdx !== null && tgtIdx >= 0 && tgtIdx < next.length) {
                const existingRows = next[tgtIdx].rows;
                const isOnlyBlank = existingRows.length === 1 && !existingRows[0].productCode;
                next[tgtIdx] = {
                  ...next[tgtIdx],
                  name: newName || next[tgtIdx].name,
                  rows: isOnlyBlank ? (newRows.length > 0 ? newRows : [makeBlankRow()]) : [...existingRows, ...newRows],
                };
              } else {
                const tgtId = next.length > 0 ? Math.max(...next.map(s => s.id)) + 1 : 1;
                const tgtSel = {
                  id: tgtId,
                  name: newName || `Selection ${tgtId}`,
                  discountType: srcSel.discountType,
                  rows: newRows.length > 0 ? newRows : [makeBlankRow()],
                };
                next.push(tgtSel);
              }
              return next;
            });
          }}
          onUpdateRowCommand={(selectionIndex, serialNumber, sku, quantity, discountPercent, discountPrice) => {
            isDirtyRef.current = true;
            setSelections(prev => prev.map((sel, i) => {
              if (selectionIndex !== undefined && selectionIndex !== null && i !== selectionIndex) return sel;
              if ((selectionIndex === undefined || selectionIndex === null) && sel.id !== currentSelId) return sel;
              return {
                ...sel,
                rows: (sel.rows || []).map((p, rowIdx) => {
                  if (serialNumber !== undefined && serialNumber !== null && (rowIdx + 1) !== serialNumber) return p;
                  if (sku !== undefined && sku !== null && p.productCode !== sku && p.sku !== sku && !p.productName?.toLowerCase().includes(sku?.toLowerCase())) return p;
                  let updatedRow = p;
                  if (quantity !== undefined && quantity !== null) {
                    updatedRow = calculateRow(updatedRow, 'quantity', quantity, gstTypeRef.current, sel.discountType);
                  }
                  if (discountPercent > 0 || (discountPercent === 0 && discountPrice === 0)) {
                    updatedRow = calculateRow(updatedRow, 'discountPercent', discountPercent || 0, gstTypeRef.current, 'percent');
                  } else if (discountPrice > 0) {
                    updatedRow = calculateRow(updatedRow, 'discountPrice', discountPrice, gstTypeRef.current, 'price');
                  }
                  return updatedRow;
                })
              };
            }));
          }}
          onDeleteRowCommand={(selectionIndex, serialNumber, sku) => {
            isDirtyRef.current = true;
            setSelections(prev => prev.map((sel, i) => {
              if (selectionIndex !== undefined && selectionIndex !== null && i !== selectionIndex) return sel;
              if ((selectionIndex === undefined || selectionIndex === null) && sel.id !== currentSelId) return sel;
              return {
                ...sel,
                rows: (sel.rows || []).filter((p, rowIdx) => {
                  if (serialNumber !== undefined && serialNumber !== null && (rowIdx + 1) === serialNumber) return false;
                  if (sku !== undefined && sku !== null && (p.productCode === sku || p.sku === sku || p.productName?.toLowerCase().includes(sku?.toLowerCase()))) return false;
                  return true;
                })
              };
            }));
          }}
          onAddMultiSelections={handleAiMultiSelections}
          onConfirmPreview={(finalPayload) => {
            if (finalPayload) {
              commitAiMultiSelections(finalPayload);
            }
          }}
          onSetGstType={(type) => {
            isDirtyRef.current = true;
            setGstType(type);
          }}
          onUpdateDiscount={(scope, index, discountPercent, discountPrice, sku) => {
            isDirtyRef.current = true;
            setSelections(prev => prev.map((sel, i) => {
              if (scope === 'selection' && i !== index) return sel;
              return {
                ...sel,
                rows: (sel.rows || []).map(p => {
                  if (scope === 'sku' && p.productCode !== sku && p.sku !== sku && !p.productName?.toLowerCase().includes(sku?.toLowerCase())) {
                    return p;
                  }
                  let updatedRow = p;
                  if (discountPercent > 0 || (discountPercent === 0 && discountPrice === 0)) {
                    updatedRow = calculateRow(updatedRow, 'discountPercent', discountPercent || 0, gstTypeRef.current, 'percent');
                  } else if (discountPrice > 0) {
                    updatedRow = calculateRow(updatedRow, 'discountPrice', discountPrice, gstTypeRef.current, 'price');
                  }
                  return updatedRow;
                })
              };
            }));
          }}
          onRenameSelection={(index, newName) => {
            isDirtyRef.current = true;
            setSelections(prev => prev.map((sel, i) => i === index ? { ...sel, name: newName } : sel));
          }}
          onSaveQuote={(type) => saveQuote(type)}
          onSaveDraft={() => setIsDraftOpen(true)}
        />
      )}

      {errorMessages.length > 0 && (
        <NotificationDrawer
          initialErrorMessages={errorMessages}
          selectionId={currentSel.id}
          onRemoveMessage={removeErrorMessage}
        />
      )}




    </div>
  );
}